import React, { useState, useEffect, useCallback } from 'react';
import { rbacAPI, authAPI, schoolConfigAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Shield, Users, ChevronDown, ChevronRight, Plus, Trash2,
  Search, X, BookOpen, MapPin, User, Lock, CheckCircle, KeyRound, Eye, EyeOff,
  Network, Loader2, Edit2, Check,
} from 'lucide-react';

const GRADE_OPTIONS = ['Nursery','LKG','UKG','I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'];
const GRADE_SORT_ORDER = [
  'Nursery','LKG','UKG','KG 1','KG 2','KG1','KG2',
  'I','II','III','IV','V',
  'VI','VI-ICSE','VII','VII-ICSE','VIII','VIII-ICSE',
  'IX','IX-ICSE','X','X-ICSE','XI','XII',
];
const PROGRAMME_OPTIONS = ['EYC','PYP','MYP','DP',''];
const STAFF_CATEGORIES  = ['teaching', 'non_teaching', 'admin'];
const PROGRAMME_TYPES   = ['EYC', 'PYP', 'MYP', 'DP', ''];

const ROLE_META = {
  super_admin:    { label: 'Super Admin',    color: 'bg-red-100 text-red-700',       desc: 'Full system access' },
  admin:          { label: 'Admin',          color: 'bg-orange-100 text-orange-700', desc: 'All modules, manage users' },
  principal:      { label: 'Principal',      color: 'bg-purple-100 text-purple-700', desc: 'School-wide full access' },
  vice_principal: { label: 'Vice Principal', color: 'bg-violet-100 text-violet-700', desc: 'School-wide, limited write' },
  coordinator:    { label: 'Coordinator',    color: 'bg-blue-100 text-blue-700',     desc: 'Assigned grades only' },
  class_teacher:  { label: 'Class Teacher',  color: 'bg-teal-100 text-teal-700',     desc: 'Assigned grade + section' },
  subject_teacher:{ label: 'Subject Teacher',color: 'bg-emerald-100 text-emerald-700', desc: 'Assigned subjects only' },
  management:     { label: 'Management',     color: 'bg-slate-100 text-slate-700',   desc: 'Read-only, all modules' },
  user:           { label: 'User',           color: 'bg-gray-100 text-gray-600',     desc: 'Standard access' },
};

const SCOPE_NEEDED    = new Set(['class_teacher', 'subject_teacher', 'coordinator']);
const SCHOOL_WIDE_ROLES = new Set(['admin', 'super_admin', 'management', 'principal', 'vice_principal']);

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || { label: role, color: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>;
}

function ScopeTag({ item, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">
      <MapPin size={10} />
      {item.grade}{item.section ? `–${item.section}` : ''}{item.subject_name ? ` · ${item.subject_name}` : ''}
      {onRemove && (
        <button onClick={() => onRemove(item.id)} className="hover:text-red-600 ml-0.5">
          <X size={10} />
        </button>
      )}
    </span>
  );
}

function AddScopeForm({ userId, userRole, gradesSections, subjects, onAdded }) {
  const [grade, setGrade]     = useState('');
  const [section, setSection] = useState('');
  const [subjectId, setSubId] = useState('');
  const [saving, setSaving]   = useState(false);

  const availableSections = grade ? (gradesSections[grade] || []) : [];
  const needsSubject = userRole === 'subject_teacher';

  const handleAdd = async () => {
    if (!grade) return toast.error('Select a grade');
    setSaving(true);
    try {
      await rbacAPI.addUserScope({ user_id: userId, grade, section: section || null, subject_id: subjectId || null });
      toast.success('Scope added');
      setGrade(''); setSection(''); setSubId('');
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add scope');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200 mt-2">
      <div>
        <label className="block text-xs text-slate-500 mb-1">Grade</label>
        <select value={grade} onChange={e => { setGrade(e.target.value); setSection(''); }}
          className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white min-w-[100px]">
          <option value="">— Select —</option>
          {Object.keys(gradesSections).map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      {availableSections.length > 0 && (
        <div>
          <label className="block text-xs text-slate-500 mb-1">Section</label>
          <select value={section} onChange={e => setSection(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
            <option value="">All sections</option>
            {availableSections.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      {needsSubject && (
        <div>
          <label className="block text-xs text-slate-500 mb-1">Subject</label>
          <select value={subjectId} onChange={e => setSubId(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white min-w-[140px]">
            <option value="">— Select subject —</option>
            {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}
      <button onClick={handleAdd} disabled={saving || !grade}
        className="flex items-center gap-1.5 text-sm bg-blue-600 text-white rounded-lg px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50">
        <Plus size={14} /> Add
      </button>
    </div>
  );
}

function ResetPasswordForm({ userId }) {
  const [pw,      setPw]      = useState('');
  const [confirm, setConfirm] = useState('');
  const [show,    setShow]    = useState(false);
  const [saving,  setSaving]  = useState(false);

  const handleReset = async () => {
    if (pw.length < 6)         return toast.error('Password must be at least 6 characters');
    if (pw !== confirm)        return toast.error('Passwords do not match');
    setSaving(true);
    try {
      await authAPI.updateUser(userId, { password: pw });
      toast.success('Password updated');
      setPw(''); setConfirm('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update password');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 mb-3">
      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
        <KeyRound size={12} className="text-slate-500" /> Reset Password
      </p>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={pw}
            onChange={e => setPw(e.target.value)}
            placeholder="New password"
            autoComplete="new-password"
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 pr-7 bg-white w-40"
          />
          <button type="button" onClick={() => setShow(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {show ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        </div>
        <input
          type={show ? 'text' : 'password'}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          placeholder="Confirm password"
          autoComplete="new-password"
          className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white w-40"
        />
        <button
          onClick={handleReset}
          disabled={saving || !pw || !confirm}
          className="flex items-center gap-1.5 text-sm bg-slate-700 text-white rounded-lg px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
        >
          <KeyRound size={13} /> {saving ? 'Saving…' : 'Set Password'}
        </button>
      </div>
    </div>
  );
}

function ClassTeacherToggleCard({ u, classes, onChanged }) {
  const [enabled,         setEnabled]         = useState(!!u.class_teacher_class_id);
  const [selectedClassId, setSelectedClassId] = useState(
    u.class_teacher_class_id ? String(u.class_teacher_class_id) : ''
  );
  const [saving, setSaving] = useState(false);

  if (!u.employee_id) return null;

  const savedClassId     = u.class_teacher_class_id ? String(u.class_teacher_class_id) : '';
  const hasUnsavedChange = enabled && !!selectedClassId && selectedClassId !== savedClassId;

  const handleToggle = async () => {
    if (enabled && u.class_teacher_class_id) {
      setSaving(true);
      try {
        await schoolConfigAPI.setClassTeacher(String(u.class_teacher_class_id), { employeeId: null });
        toast.success('Class teacher assignment removed');
        onChanged();
      } catch { toast.error('Failed to update'); }
      finally { setSaving(false); }
    }
    setEnabled(v => !v);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await schoolConfigAPI.setClassTeacher(selectedClassId, { employeeId: u.employee_id });
      toast.success('Class teacher assigned');
      onChanged();
    } catch { toast.error('Failed to update class teacher'); }
    finally { setSaving(false); }
  };

  return (
    <div className={`border rounded-xl px-3 py-2.5 transition-colors h-full ${enabled ? 'bg-teal-50/60 border-teal-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <User size={12} className={enabled ? 'text-teal-600' : 'text-slate-400'} /> Class Teacher
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {enabled && u.class_teacher_grade
              ? `Grade ${u.class_teacher_grade}${u.class_teacher_section ? ` – ${u.class_teacher_section}` : ''}`
              : 'Not assigned as class teacher'}
          </p>
        </div>
        <button onClick={handleToggle} disabled={saving}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${enabled ? 'bg-teal-500' : 'bg-slate-300'} ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${enabled ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
      </div>
      {enabled && (
        <div className="mt-2.5 pt-2.5 border-t border-teal-100">
          <label className="block text-[10px] text-slate-500 mb-1.5">Select Grade &amp; Section</label>
          <div className="flex items-center gap-2">
            <select
              value={selectedClassId}
              onChange={e => setSelectedClassId(e.target.value)}
              disabled={saving}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white min-w-[180px] disabled:opacity-50"
            >
              <option value="">— Select Class —</option>
              {classes.map(c => (
                <option key={c.id} value={String(c.id)}>
                  {c.grade}{c.section ? ` – ${c.section}` : ''}
                  {c.teacher_name && c.id !== u.class_teacher_class_id ? ` (${c.teacher_name})` : ''}
                </option>
              ))}
            </select>
            {hasUnsavedChange && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="text-xs px-3 py-1.5 rounded-lg bg-teal-500 text-white font-medium hover:bg-teal-600 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GradeMultiSelect({ gradeOnlyScopes, gradesSections, userId, onSaved }) {
  const existingGrades = new Set(gradeOnlyScopes.map(s => s.grade));
  const [selected, setSelected] = useState(new Set(existingGrades));
  const [saving,   setSaving]   = useState(false);

  const allGrades = Object.keys(gradesSections).sort((a, b) => {
    const ai = GRADE_SORT_ORDER.indexOf(a);
    const bi = GRADE_SORT_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  const hasChanges = allGrades.some(g => selected.has(g) !== existingGrades.has(g));

  const toggle = (grade) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(grade)) next.delete(grade); else next.add(grade);
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const toAdd    = allGrades.filter(g => selected.has(g) && !existingGrades.has(g));
      const toRemove = gradeOnlyScopes.filter(s => !selected.has(s.grade));
      await Promise.all([
        ...toAdd.map(grade => rbacAPI.addUserScope({ user_id: userId, grade, section: null, subject_id: null })),
        ...toRemove.map(s => rbacAPI.removeUserScope(s.id)),
      ]);
      toast.success('Grades updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save grades');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
          Assign to Grades
        </span>
        <button type="button" onClick={() => setSelected(new Set(allGrades))}
          className="text-xs text-primary-600 hover:text-primary-800 underline">
          Select All
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {allGrades.map(grade => (
          <button key={grade} type="button" onClick={() => toggle(grade)}
            className={`px-2.5 py-1 text-xs font-semibold rounded-full border transition-all ${
              selected.has(grade)
                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                : 'bg-white text-slate-500 border-slate-200 hover:border-primary-300 hover:text-primary-600'
            }`}>
            {grade}
          </button>
        ))}
      </div>
      {hasChanges && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setSelected(new Set(existingGrades))}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition">
            {saving && <Loader2 size={11} className="animate-spin" />}
            Save
          </button>
        </div>
      )}
    </div>
  );
}

function CoordinatorToggleCard({ userId, gradeOnlyScopes, gradesSections, onSaved }) {
  const [open, setOpen] = useState(gradeOnlyScopes.length > 0);

  // Sync toggle state when grades arrive asynchronously after mount
  useEffect(() => {
    if (gradeOnlyScopes.length > 0) setOpen(true);
  }, [gradeOnlyScopes.length]);

  return (
    <div className={`border rounded-xl px-3 py-2.5 transition-colors ${open ? 'bg-blue-50/50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <MapPin size={12} className={open ? 'text-blue-600' : 'text-slate-400'} /> Coordinator
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {gradeOnlyScopes.length > 0
              ? `${gradeOnlyScopes.length} grade${gradeOnlyScopes.length > 1 ? 's' : ''} assigned`
              : 'No grades assigned'}
          </p>
        </div>
        <button onClick={() => setOpen(v => !v)}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${open ? 'bg-blue-500' : 'bg-slate-300'}`}>
          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${open ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
      </div>
      {open && (
        <div className="mt-2.5 pt-2.5 border-t border-blue-100">
          <GradeMultiSelect
            key={gradeOnlyScopes.map(s => s.id).join(',')}
            gradeOnlyScopes={gradeOnlyScopes}
            gradesSections={gradesSections}
            userId={userId}
            onSaved={onSaved}
          />
        </div>
      )}
    </div>
  );
}

function ObserverToggleCard({ u, gradeOnlyScopes, gradesSections, onToggleChanged, onSaved }) {
  const [active, setActive] = useState(!!u.is_observer);
  const [saving, setSaving] = useState(false);

  if (!u.employee_id) return null;

  const handleToggle = async () => {
    const next = !active;
    setSaving(true);
    try {
      await schoolConfigAPI.setObserver(u.employee_id, { isObserver: next });
      setActive(next);
      toast.success(next ? 'Set as observer' : 'Observer removed');
      // Don't call onToggleChanged here — it triggers a full user-list reload
      // which collapses the expanded row before the user can select grades.
      // The parent will get the updated is_observer value on next natural reload.
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update');
    } finally { setSaving(false); }
  };

  return (
    <div className={`border rounded-xl px-3 py-2.5 transition-colors h-full ${active ? 'bg-purple-50/50 border-purple-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Eye size={12} className={active ? 'text-purple-600' : 'text-slate-400'} /> Observer
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {active
              ? gradeOnlyScopes.length > 0
                ? `Observing ${gradeOnlyScopes.length} grade${gradeOnlyScopes.length > 1 ? 's' : ''}`
                : 'Active — no grades set'
              : 'Not set as an observer'}
          </p>
        </div>
        <button onClick={handleToggle} disabled={saving}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${active ? 'bg-purple-500' : 'bg-slate-300'} ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${active ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
      </div>
      {active && (
        <div className="mt-2.5 pt-2.5 border-t border-purple-100">
          <GradeMultiSelect
            key={gradeOnlyScopes.map(s => s.id).join(',')}
            gradeOnlyScopes={gradeOnlyScopes}
            gradesSections={gradesSections}
            userId={u.id}
            onSaved={onSaved}
          />
        </div>
      )}
    </div>
  );
}

function HierarchyInline({ u, employees, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({});

  if (!u.employee_id) return null;

  const startEdit = () => {
    setForm({
      reports_to_id:  u.reports_to_id  ?? '',
      staff_category: u.staff_category || 'teaching',
      programme_type: u.programme_type || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await schoolConfigAPI.updateEmployeeHierarchy(u.employee_id, {
        reports_to_id:  form.reports_to_id  ? parseInt(form.reports_to_id) : null,
        staff_category: form.staff_category || null,
        programme_type: form.programme_type || null,
      });
      toast.success('Staff details updated');
      setEditing(false);
      onChanged();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 mb-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <Network size={12} className="text-slate-500" /> Staff Details
        </p>
        {editing ? (
          <div className="flex gap-1.5">
            <button onClick={handleSave} disabled={saving}
              className="text-emerald-600 hover:text-emerald-700 p-1 rounded hover:bg-emerald-50 transition">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            </button>
            <button onClick={() => setEditing(false)}
              className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition">
              <X size={12} />
            </button>
          </div>
        ) : (
          <button onClick={startEdit}
            className="text-slate-400 hover:text-primary-600 p-1 rounded hover:bg-primary-50 transition">
            <Edit2 size={12} />
          </button>
        )}
      </div>

      {editing ? (
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="block text-[10px] text-slate-500 mb-1">Reports To</label>
            <select value={form.reports_to_id}
              onChange={e => setForm(f => ({ ...f, reports_to_id: e.target.value }))}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white min-w-[150px]">
              <option value="">— None —</option>
              {employees.filter(e => e.id !== u.employee_id).map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-slate-500 mb-1">Category</label>
            <select value={form.staff_category}
              onChange={e => setForm(f => ({ ...f, staff_category: e.target.value }))}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              {STAFF_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-slate-500 mb-1">Programme</label>
            <select value={form.programme_type}
              onChange={e => setForm(f => ({ ...f, programme_type: e.target.value }))}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="">—</option>
              {PROGRAMME_TYPES.filter(Boolean).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <span>
            <span className="text-slate-400">Reports to: </span>
            {u.reports_to_name || <span className="text-slate-300">—</span>}
          </span>
          <span className={`px-2 py-0.5 rounded-full font-medium border ${
            u.staff_category === 'teaching'     ? 'bg-blue-50 text-blue-700 border-blue-100' :
            u.staff_category === 'non_teaching' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                  'bg-slate-50 text-slate-600 border-slate-200'
          }`}>
            {(u.staff_category || 'teaching').replace('_', ' ')}
          </span>
          {u.programme_type && (
            <span className="font-semibold text-primary-700">{u.programme_type}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Employee with no login account ────────────────────────────────────────────
function UnlinkedEmployeeRow({ emp, allRoles, onCreated }) {
  const [open,    setOpen]    = useState(false);
  const [role,    setRole]    = useState('');
  const [pwd,     setPwd]     = useState('Admin@123');
  const [showPwd, setShowPwd] = useState(false);
  const [saving,  setSaving]  = useState(false);

  const handleCreate = async () => {
    if (!role) return toast.error('Select a role');
    setSaving(true);
    try {
      await authAPI.createUser({ name: emp.name, email: emp.email, password: pwd, role });
      toast.success(`Account created for ${emp.name}`);
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to create account');
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-dashed border-slate-300 rounded-xl bg-slate-50/60 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
          <span className="text-slate-500 text-xs font-bold">{emp.name?.[0]?.toUpperCase() || '?'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700">{emp.name}</span>
            <span className="text-[10px] font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5">No Account</span>
          </div>
          <p className="text-xs text-slate-400">{emp.email}{emp.designation ? ` · ${emp.designation}` : ''}</p>
        </div>
        <button onClick={() => setOpen(v => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-primary-600 border border-primary-200 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors">
          <Plus size={12} /> Create Account
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 px-4 py-3 bg-white flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 min-w-[160px]">
              <option value="">— Select role —</option>
              {allRoles.filter(r => r.name !== 'super_admin').map(r => (
                <option key={r.name} value={r.name}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Default Password</label>
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} value={pwd} onChange={e => setPwd(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 pr-8 w-44" />
              <button onClick={() => setShowPwd(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                {showPwd ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          </div>
          <button onClick={handleCreate} disabled={saving}
            className="flex items-center gap-1.5 text-sm font-medium bg-primary-600 text-white px-4 py-1.5 rounded-lg hover:bg-primary-700 disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
            Create
          </button>
          <button onClick={() => setOpen(false)} className="text-sm text-slate-400 hover:text-slate-600 px-2 py-1.5">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function UserRow({ u, gradesSections, subjects, classes, employees, allRoles, onRoleChanged, onScopeChanged, onClassTeacherChanged, onHierarchyChanged }) {
  const [expanded,     setExpanded]     = useState(false);
  const [scopes,       setScopes]       = useState([]);
  const [scopesLoaded, setScopesLoaded] = useState(false);
  const [roleChanging, setRoleChanging] = useState(false);
  const [sdAccess,     setSdAccess]     = useState(!!(u.school_dashboard_access));
  const [sdChanging,   setSdChanging]   = useState(false);
  const { user: me } = useAuth();
  const isSchoolWideRole = SCHOOL_WIDE_ROLES.has(u.role);

  const isSelf      = me?.id === u.id;
  const isSuperAdmin = u.role === 'super_admin';
  const scopeNeeded  = SCOPE_NEEDED.has(u.role);
  // Use server-provided count for collapsed badge (avoids stale state)
  const scopeCount   = scopesLoaded ? scopes.length : (u.scope_count ?? 0);

  const loadScopes = useCallback(async () => {
    try {
      const res = await rbacAPI.getUserScope(u.id);
      setScopes(res.data);
      setScopesLoaded(true);
    } catch { /* */ }
  }, [u.id]);

  // Load scope detail whenever the row is expanded
  useEffect(() => {
    if (expanded) loadScopes();
  }, [expanded, loadScopes]);

  const handleRoleChange = async (newRole) => {
    if (newRole === u.role) return;
    setRoleChanging(true);
    try {
      await rbacAPI.setUserRole(u.id, newRole);
      toast.success(`${u.name} → ${ROLE_META[newRole]?.label || newRole}`);
      onRoleChanged(u.id, newRole);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to change role');
    } finally { setRoleChanging(false); }
  };

  const handleRemoveScope = async (scopeId) => {
    try {
      await rbacAPI.removeUserScope(scopeId);
      setScopes(s => s.filter(x => x.id !== scopeId));
      onScopeChanged();
    } catch { toast.error('Failed to remove scope'); }
  };

  const handleSdAccessToggle = async () => {
    if (isSchoolWideRole || sdChanging) return;
    setSdChanging(true);
    const next = !sdAccess;
    try {
      await rbacAPI.setSchoolDashboardAccess(u.id, next);
      setSdAccess(next);
      toast.success(next ? `${u.name} can now view School Dashboard` : `School Dashboard access removed for ${u.name}`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update access');
    } finally { setSdChanging(false); }
  };

  const gradeOnlyScopes = scopes.filter(s => !s.subject_id);
  const subjectScopes   = scopes.filter(s =>  !!s.subject_id);

  return (
    <div className={`border border-slate-200 rounded-xl overflow-hidden transition-all ${expanded ? 'shadow-sm' : ''}`}>
      <div
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${expanded ? 'bg-slate-50' : 'bg-white'}`}
        onClick={() => setExpanded(v => !v)}
      >
        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <span className="text-primary-700 text-xs font-bold">{u.name?.[0]?.toUpperCase() || '?'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800 truncate">{u.name}</span>
            {isSelf && <span className="text-[10px] text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">You</span>}
          </div>
          <p className="text-xs text-slate-500 truncate">{u.email}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          {u.class_teacher_class_id && (
            <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">Class Teacher</span>
          )}
          {u.coordinator_grade_count > 0 && (
            <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">Coordinator</span>
          )}
          {u.is_observer && (
            <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">Observer</span>
          )}
          {scopeNeeded && scopeCount === 0 && (
            <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">No scope</span>
          )}
          <RoleBadge role={u.role} />
          {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-3 bg-white border-t border-slate-100 space-y-2.5">

          {/* 1. PASSWORD RESET — first */}
          {!isSuperAdmin && <ResetPasswordForm userId={u.id} />}

          {/* 2+3. 2×2 GRID: [Role & Dashboard] [Class Teacher] / [Coordinator] [Observer] */}
          <div className="grid grid-cols-2 gap-2">
            {/* Cell 1: Role + Dashboard Access */}
            <div className="flex flex-col gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 h-full">
              <div>
                <p className="text-xs text-slate-500 mb-1">Role</p>
                {isSuperAdmin || isSelf ? (
                  <RoleBadge role={u.role} />
                ) : (
                  <select value={u.role} onChange={e => handleRoleChange(e.target.value)} disabled={roleChanging}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white w-full">
                    {allRoles.map(r => (
                      <option key={r.name} value={r.name} disabled={r.name === 'super_admin'}>
                        {ROLE_META[r.name]?.label || r.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {!u.is_active && (
                <span className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 self-start">Inactive</span>
              )}
            </div>

            {/* Cell 2: Class Teacher */}
            {u.employee_id
              ? <ClassTeacherToggleCard u={u} classes={classes} onChanged={onClassTeacherChanged} />
              : <div />}

            {/* Cell 3: Coordinator */}
            {u.employee_id && (
              <CoordinatorToggleCard
                userId={u.id}
                gradeOnlyScopes={gradeOnlyScopes}
                gradesSections={gradesSections}
                onSaved={loadScopes}
              />
            )}

            {/* Cell 4: Observer */}
            {u.employee_id && (
              <ObserverToggleCard
                u={u}
                gradeOnlyScopes={gradeOnlyScopes}
                gradesSections={gradesSections}
                onToggleChanged={onScopeChanged}
                onSaved={loadScopes}
              />
            )}
          </div>

          {/* 4. SUBJECT ASSIGNMENTS (subject_teacher only) */}
          {u.role === 'subject_teacher' && (
            <div className="pt-1">
              <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                <BookOpen size={12} className="text-emerald-500" /> Subject Assignments
              </p>
              {subjectScopes.length === 0 ? (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                  ⚠ No subjects assigned yet.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {subjectScopes.map(s => <ScopeTag key={s.id} item={s} onRemove={handleRemoveScope} />)}
                </div>
              )}
              <AddScopeForm userId={u.id} userRole={u.role} gradesSections={gradesSections} subjects={subjects} onAdded={loadScopes} />
            </div>
          )}

        </div>
      )}
    </div>
  );
}

export default function UserAccessControl() {
  const [users,          setUsers]          = useState([]);
  const [roles,          setRoles]          = useState([]);
  const [gradesSections, setGradesSections] = useState({});
  const [subjects,       setSubjects]       = useState([]);
  const [classes,        setClasses]        = useState([]);
  const [employees,      setEmployees]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [filterRole,     setFilterRole]     = useState('');
  const [activeTab,      setActiveTab]      = useState('users'); // 'users' | 'roles'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, rRes, gsRes, sRes, clsRes, empRes] = await Promise.all([
        rbacAPI.listUsers(),
        rbacAPI.listRoles(),
        rbacAPI.getGradesSections(),
        rbacAPI.getSubjects(),
        schoolConfigAPI.getClassesWithTeachers(),
        schoolConfigAPI.getStaff(),
      ]);
      setUsers(uRes.data);
      setRoles(rRes.data);
      setGradesSections(gsRes.data);
      setSubjects(sRes.data);
      setClasses(clsRes.data);
      setEmployees((empRes.data || []).filter(e => e.is_active));
    } catch { toast.error('Failed to load access data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRoleChanged = (userId, newRole) => {
    setUsers(us => us.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const filtered = users.filter(u => {
    const matchSearch = !search
      || (u.name || '').toLowerCase().includes(search.toLowerCase())
      || (u.email || '').toLowerCase().includes(search.toLowerCase());
    const matchRole = !filterRole || u.role === filterRole || (!u.has_account && filterRole === '__no_account__');
    return matchSearch && matchRole;
  });

  const scopeWarnings = users.filter(u => u.has_account && SCOPE_NEEDED.has(u.role) && (u.scope_count ?? 0) === 0);
  const noAccountCount = users.filter(u => !u.has_account).length;

  return (
    <div className="p-6 space-y-5 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield size={22} className="text-primary-600" />
            User Access Control
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Assign roles and data scope to staff members</p>
        </div>
        <div className="flex items-center gap-2 border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
          {[
            { id: 'users', label: 'Users', icon: Users },
            { id: 'roles', label: 'Roles', icon: Lock },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === t.id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Warnings */}
      {scopeWarnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-sm font-semibold text-amber-800 mb-1">⚠ {scopeWarnings.length} user{scopeWarnings.length > 1 ? 's' : ''} without scope assignment</p>
          <p className="text-xs text-amber-700">
            {scopeWarnings.map(u => u.name).join(', ')} — class/subject/coordinator roles need scope to control data visibility.
          </p>
        </div>
      )}

      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                autoComplete="off"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
              />
            </div>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
              className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white">
              <option value="">All</option>
              {roles.map(r => <option key={r.name} value={r.name}>{ROLE_META[r.name]?.label || r.name} ({r.user_count})</option>)}
              {noAccountCount > 0 && <option value="__no_account__">No Account ({noAccountCount})</option>}
            </select>
            <span className="text-xs text-slate-400">{filtered.length} of {users.length} staff</span>
          </div>

          {/* Users List */}
          {loading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
          ) : (
            <div className="space-y-2">
              {filtered.map(u => (
                u.has_account
                  ? <UserRow
                      key={u.id}
                      u={u}
                      gradesSections={gradesSections}
                      subjects={subjects}
                      classes={classes}
                      employees={employees}
                      allRoles={roles}
                      onRoleChanged={handleRoleChanged}
                      onScopeChanged={load}
                      onClassTeacherChanged={load}
                      onHierarchyChanged={load}
                    />
                  : <UnlinkedEmployeeRow
                      key={`emp-${u.employee_id}`}
                      emp={u}
                      allRoles={roles}
                      onCreated={load}
                    />
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-12 text-slate-400 text-sm">No staff match this filter.</div>
              )}
            </div>
          )}

        </>
      )}

      {activeTab === 'roles' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(r => {
            const meta = ROLE_META[r.name] || { label: r.name, color: 'bg-gray-100 text-gray-600', desc: '' };
            return (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold px-2.5 py-1 rounded-full ${meta.color}`}>{meta.label}</span>
                  <span className="text-xs text-slate-400">{r.user_count} user{r.user_count !== 1 ? 's' : ''}</span>
                </div>
                <p className="text-xs text-slate-500">{meta.desc || r.description || '—'}</p>
                <div className="flex items-center gap-2 pt-1">
                  <BookOpen size={12} className="text-slate-400" />
                  <span className="text-xs text-slate-500">{r.module_count} module{r.module_count !== 1 ? 's' : ''} configured</span>
                </div>
                {SCOPE_NEEDED.has(r.name) && (
                  <div className="text-xs text-blue-600 flex items-center gap-1">
                    <MapPin size={10} />
                    Requires scope assignment per user
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}


    </div>
  );
}
